import { queries } from "@/graphql";
import { useQuery } from "@apollo/client/react";
import { useEffect, useMemo } from "react";
import { enqueueSnackbar } from "notistack";
import type {
  QueryGetSubscriptionFeaturesArgs,
  SubscriptionFeaturesType,
} from "@/types/graphql";

/**
 * Custom hook to fetch the list of features.
 * @param {object} props - The properties to be passed to the query.
 * @returns {{featuresList: Array<{id: string, name: string}>, loading: boolean, error: object}} - The list of features, loading state, and error.
 */
export function useFeaturesList(props: QueryGetSubscriptionFeaturesArgs = {}) {
  const variables: QueryGetSubscriptionFeaturesArgs = {
    ...props,
  };

  const { data, loading, error } = useQuery<
    {
      getSubscriptionFeatures: Array<SubscriptionFeaturesType | null> | null;
    },
    QueryGetSubscriptionFeaturesArgs
  >(queries.getSubscriptionFeatures, {
    variables,
    errorPolicy: "all",
  });

  // Handle error
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    }
  }, [error]);

  const featuresList = useMemo(() => {
    return data?.getSubscriptionFeatures || [];
  }, [data?.getSubscriptionFeatures]);

  return {
    featuresList,
    loading,
    error,
  };
}
